'use client';

import { FormEvent } from 'react';
import { LoadingIndicator } from '@wishket/design-system';

import useProfileInput from './useProfileInput';
import { CAREER_MAP } from './Profile.constants';

import { ProfileInfoForm } from '@/features/Profile';
import {
  useLevelQuery,
  useProfileInfoMutation,
  useProfileInfoQuery,
  useRoleQuery,
} from '@/entities/profile/models';
import { useDebounce, useProfile } from '@/shared/hooks';
import { CareerItem, LevelItem, RoleItem } from '@/shared/types';

interface ProfileEditOnBoardingProps {
  onNextStep: () => void;
}

const ProfileEditOnBoarding = ({ onNextStep }: ProfileEditOnBoardingProps) => {
  const { profile } = useProfile();
  const { isLoading: isProfileInfoLoading, data: profileInfo } =
    useProfileInfoQuery({
      yozmitProfileId: profile?.yozmitProfileId,
    });
  const { data, action, keyValue, error, validate, autoSetLevel } =
    useProfileInput({
      profileInfo,
    });
  const { mutate } = useProfileInfoMutation();

  const { data: roleData } = useRoleQuery();
  const { data: levelData } = useLevelQuery();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate() || !profile?.yozmitProfileId) return;

    onNextStep();
  };

  const handleRoleChange = (role: RoleItem) => {
    if (!profile?.yozmitProfileId) return;

    action.role(role);
    mutate({
      role: String(role.key),
      yozmitProfileId: profile?.yozmitProfileId,
    });
  };

  const handleCareerChange = (career: CareerItem) => {
    if (!profile?.yozmitProfileId) return;

    action.career(career);
    mutate({
      career: Number(career.key),
      level: autoSetLevel(Number(career.key)),
      yozmitProfileId: profile?.yozmitProfileId,
    });
  };

  const debouncedAddMutate = useDebounce(
    (params: { addedSkills: string[]; yozmitProfileId: number }) =>
      mutate(params),
    200,
    { leading: false }
  );

  const handleSkillAdd = (skill: string) => {
    if (!profile?.yozmitProfileId) return;

    action.skill.handleSkillInput(skill);
    const params = {
      addedSkills: [...data.skill.addedSkills, skill.toLocaleLowerCase()],
      yozmitProfileId: profile.yozmitProfileId,
    };

    debouncedAddMutate(params);
  };

  const handleSkillRemove = (skill: string) => {
    if (!profile?.yozmitProfileId) return;

    action.skill.handleSkillDelete(skill);
    const removedSkills =
      profileInfo?.skills
        .filter((skillItem) => skillItem.name.toLocaleLowerCase() === skill)
        .map((skillItem) => skillItem.id) || [];

    mutate({
      removedSkills,
      yozmitProfileId: profile.yozmitProfileId,
    });
  };

  const handleLevelChange = (level: LevelItem) => {
    if (!profile?.yozmitProfileId) return;

    action.level(level);
    mutate({
      level: String(level.key),
      yozmitProfileId: profile?.yozmitProfileId,
    });
  };

  if (!profile?.yozmitProfileId) return;

  if (isProfileInfoLoading) return <LoadingIndicator />;

  return (
    <ProfileInfoForm
      isOnboarding
      selectedRole={keyValue(data.role, roleData?.constants || {})}
      handleRoleChange={handleRoleChange}
      roleData={roleData}
      skillData={data.skill}
      setSearchStackValue={action.skill.setSkillQuery}
      deleteStackChip={handleSkillRemove}
      skillInput={handleSkillAdd}
      selectedCareer={keyValue(data.career, CAREER_MAP)}
      handleCareerChange={handleCareerChange}
      levelData={levelData?.levelData || []}
      selectedLevel={keyValue(data.level, levelData?.constants || {})}
      handleLevelChange={handleLevelChange}
      error={error}
      onSubmit={onSubmit}
    />
  );
};

export default ProfileEditOnBoarding;
